import { useState } from 'react';
import { DetailsPanel, EditIcon, FormatInformation, IconButton } from '@weareopenr/component-library';
import styles from './ProductDetailsPanel.module.scss';
import { IMenuItemDetail } from 'src/types/menu.types';
import { ItemType, NO_INVENTORY_TYPES, REGULATORY_STATEMENTS } from 'src/constants';
import GlobalItemDetailsEditModal from '../GlobalItemDetailsEditModal';
import { hasRegulatoryStatement } from 'src/utils/regulatory-statements';
import { InfoTooltip, InfoTooltipType } from '../InfoTooltip/InfoTooltip';
import type { IAggregator } from 'src/types/aggregator.types';
import { getEnhancedBundleInstance, isEnhancedBundleFlagged } from 'src/utils/enhancedBundle';

export interface ProductDetailsPanelProps {
  item: IMenuItemDetail;
  aggregators?: IAggregator[];
}

const renderHFSS = (modalItem: IMenuItemDetail) => {
  const isHFSS = hasRegulatoryStatement(modalItem.regulatory_statements, REGULATORY_STATEMENTS.HFSS.name);
  return (
    <FormatInformation heading="Contains High Fat, Salt or Sugar" tooltip={<InfoTooltip type={InfoTooltipType.HFSS} />}>
      {isHFSS ? 'Yes' : 'No'}
    </FormatInformation>
  );
};

const ProductDetailsPanel = ({ item, aggregators }: ProductDetailsPanelProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleModalClick = () => setIsModalOpen((prev: boolean) => !prev);
  const recipe = item.recipe;
  const isBundle = item.type === ItemType.BUNDLE;

  const enhancedBundleInstance = getEnhancedBundleInstance(aggregators);
  const canShowEnhancedBundleFlag = isBundle && !!enhancedBundleInstance;
  const isEnhancedBundle = isEnhancedBundleFlagged(item.instance_configuration, enhancedBundleInstance?.instanceId);

  return (
    <>
      <GlobalItemDetailsEditModal
        isModalOpen={isModalOpen}
        onModalClose={handleModalClick}
        currentItem={item}
        aggregators={aggregators}
      />
      <DetailsPanel
        title="Product Details"
        action={
          <IconButton
            onClick={handleModalClick}
            aria-label="Edit product details"
            icon={EditIcon}
            buttonStyle="ghost"
          />
        }
      >
        <FormatInformation heading="Name">{item.name}</FormatInformation>
        {item.code && <FormatInformation heading="Reference Code">{item.code}</FormatInformation>}
        <FormatInformation heading="Consumer name">{item.consumerName || 'No Consumer Name'}</FormatInformation>
        <FormatInformation heading="Description">{item.description || 'No Description'} </FormatInformation>
        <FormatInformation heading="Product Range">
          {item.ranges?.length > 0 ? (
            <ul className={styles['range-list']}>
              {item.ranges.map((range) => (
                <li key={range.name}>{range.name}</li>
              ))}
            </ul>
          ) : (
            'No Product Range'
          )}
        </FormatInformation>
        <FormatInformation heading="Default POS ID">{item.plu || 'No Default POS ID'} </FormatInformation>
        {isBundle && (
          <FormatInformation heading="Party size">{item.party_size === null ? '-' : item.party_size}</FormatInformation>
        )}
        <FormatInformation heading="Age Verification Required?">
          {hasRegulatoryStatement(item.regulatory_statements, REGULATORY_STATEMENTS.AGE_VERIFICATION.name)
            ? 'Yes'
            : 'No'}
        </FormatInformation>
        {!NO_INVENTORY_TYPES.includes(item.type) && recipe && renderHFSS(item)}
        {canShowEnhancedBundleFlag && (
          <FormatInformation
            heading="Enhanced bundles flag"
            tooltip={<InfoTooltip type={InfoTooltipType.ENHANCED_BUNDLE} />}
          >
            {isEnhancedBundle ? 'Yes' : 'No'}
          </FormatInformation>
        )}
      </DetailsPanel>
    </>
  );
};
export default ProductDetailsPanel;
